import type { WorkSurface } from '@heidi/agents/contracts';
import {
  AGENT_CREDIT_ALLOWANCE_IN_CHAT_GAP_CLASS,
  agentCreditAllowanceBannerMessages,
  CreditAllowanceBannerNudge,
  creditAllowanceRefusalAtom,
  CreditLimitReachedUpgradeBanner,
  type AgentCreditAllowanceBannerPlacement,
  type AgentCreditAllowanceBannerSurface,
  type CreditLimitReachedUpgradeCopyOverrides,
  type CreditUsagePopoverPlan,
} from '@heidi/commerce';
import { Link } from '@heidi/design-system/link';
import { getDefaultStore, useAtomValue } from 'jotai';
import { type ReactNode } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';

import { getUsageLimitsHelpUrl } from '@heidi/commerce/data';

import {
  useAgentCreditAllowanceBannerState,
  useTrackQuotaUpgradeViewed,
} from './useAgentCreditAllowanceBannerState';

const BANNER_TEST_ID = 'agent-credit-allowance-banner';

function formatResetParts(intl: IntlShape, cycleEnd: string) {
  const resetAt = new Date(cycleEnd);
  return {
    date: intl.formatDate(resetAt, { month: 'short', day: 'numeric' }),
    time: intl.formatDate(resetAt, { hour: 'numeric', minute: '2-digit' }),
  };
}

function resetDateLabel(intl: IntlShape, cycleEnd: string | null | undefined): string {
  return cycleEnd != null
    ? formatResetParts(intl, cycleEnd).date
    : intl.formatMessage({
        defaultMessage: 'your next reset',
        id: 'QLwDB9w97C',
      });
}

function buildWorkflowCreditBannerCopy(
  intl: IntlShape,
  threshold: 90 | 100,
  cycleEnd: string | null | undefined
): CreditLimitReachedUpgradeCopyOverrides {
  const date = resetDateLabel(intl, cycleEnd);

  if (threshold === 90) {
    return {
      title: agentCreditAllowanceBannerMessages.workflowAlmostTitle,
      description: intl.formatMessage(
        agentCreditAllowanceBannerMessages.workflowAlmostDescription,
        { date }
      ),
      ctaLabel: agentCreditAllowanceBannerMessages.workflowUpgradeCta,
    };
  }

  return {
    title: agentCreditAllowanceBannerMessages.workflowReachedTitle,
    description: intl.formatMessage(
      agentCreditAllowanceBannerMessages.workflowReachedDescription,
      { date }
    ),
    ctaLabel: agentCreditAllowanceBannerMessages.workflowUpgradeCta,
  };
}

function buildInChatReachedCreditBannerCopy(
  intl: IntlShape,
  cycleEnd: string | null | undefined,
  learnMoreHref: string
): CreditLimitReachedUpgradeCopyOverrides {
  return {
    title: agentCreditAllowanceBannerMessages.exhaustedTitle,
    description: (
      <FormattedMessage
        {...agentCreditAllowanceBannerMessages.exhaustedDescription}
        values={{
          date: resetDateLabel(intl, cycleEnd),
          learnMore: (chunks: ReactNode) => (
            <Link
              variant="inline"
              href={learnMoreHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </Link>
          ),
        }}
      />
    ),
    ctaLabel: agentCreditAllowanceBannerMessages.exhaustedUpgradeCta,
  };
}

function buildLegacyInChatReachedCreditBannerCopy(
  intl: IntlShape,
  cycleEnd: string | null | undefined
): CreditLimitReachedUpgradeCopyOverrides {
  const resetParts = cycleEnd != null ? formatResetParts(intl, cycleEnd) : null;
  return {
    title: agentCreditAllowanceBannerMessages.legacyExhaustedTitle,
    ...(resetParts != null
      ? {
          description: intl.formatMessage(
            agentCreditAllowanceBannerMessages.legacyExhaustedDescription,
            resetParts
          ),
        }
      : {}),
  };
}

export type AgentCreditAllowanceBannerViewProps = {
  surface: AgentCreditAllowanceBannerSurface;
  /** Max plan the banner sells; Practice seats get Practice Max copy and tone. */
  plan?: CreditUsagePopoverPlan;
  hideCTA?: boolean;
  cycleEnd?: string | null;
  onDismiss?: () => void;
  onUpgradeClick?: () => void;
  className?: string;
  /** Optional copy overrides (e.g. routines-page / in-chat Figma strings). */
  copy?: CreditLimitReachedUpgradeCopyOverrides;
  /**
   * Usage-nudges ON: Evidence-continue copy + Upgrade CTA. Off keeps the old
   * exhausted banner without multiplier upsell copy.
   */
  isUsageNudgesEnabled?: boolean;
};

/** Forced-surface render — live hook and `/dev` fixtures both drive this. */
export function AgentCreditAllowanceBannerView({
  surface,
  plan = 'clinicianMax',
  hideCTA = false,
  cycleEnd,
  onDismiss,
  onUpgradeClick,
  className,
  copy: copyOverride,
  isUsageNudgesEnabled = false,
}: AgentCreditAllowanceBannerViewProps) {
  const intl = useIntl();

  const exhaustedCopy =
    surface.threshold === 100 && copyOverride == null
      ? isUsageNudgesEnabled
        ? buildInChatReachedCreditBannerCopy(
            intl,
            cycleEnd,
            getUsageLimitsHelpUrl(intl.locale)
          )
        : buildLegacyInChatReachedCreditBannerCopy(intl, cycleEnd)
      : undefined;

  return (
    <CreditLimitReachedUpgradeBanner
      plan={plan}
      threshold={surface.threshold}
      hideCTA={hideCTA}
      dismissible={surface.dismissible}
      onDismiss={surface.dismissible ? onDismiss : undefined}
      onUpgradeClick={onUpgradeClick}
      copy={copyOverride ?? exhaustedCopy}
      className={className}
      data-testid={BANNER_TEST_ID}
    />
  );
}

type AgentCreditAllowanceBannerProps = {
  className?: string;
  /** Where the banner renders, for work_upgrade_actioned. */
  analyticsSurface?: WorkSurface;
};

/** Account-level credit nudge — app-owned wrapper over the Growth banner. */
export function AgentCreditAllowanceBanner({
  className,
  analyticsSurface = 'project_chat',
}: AgentCreditAllowanceBannerProps) {
  const intl = useIntl();
  const {
    surface,
    plan,
    hideCTA,
    cycleEnd,
    onDismiss,
    onUpgradeClick,
    isUsageNudgesEnabled,
  } = useAgentCreditAllowanceBannerState(analyticsSurface);
  useTrackQuotaUpgradeViewed(surface, analyticsSurface, plan);

  if (surface == null) {
    return null;
  }

  const isWorkflow = analyticsSurface === 'workflow';
  const workflowCopy =
    isWorkflow && (surface.threshold === 90 || surface.threshold === 100)
      ? buildWorkflowCreditBannerCopy(intl, surface.threshold, cycleEnd)
      : undefined;

  return (
    <AgentCreditAllowanceBannerView
      surface={surface}
      plan={plan}
      hideCTA={hideCTA}
      cycleEnd={cycleEnd}
      onDismiss={onDismiss}
      onUpgradeClick={onUpgradeClick}
      className={className}
      copy={workflowCopy}
      isUsageNudgesEnabled={isUsageNudgesEnabled}
    />
  );
}

/** Composer dock keeps the 16px gap; transcript placement uses the timeline gap. */
export function AgentCreditAllowanceInChatBanner({
  placement = 'composer',
}: {
  placement?: AgentCreditAllowanceBannerPlacement;
}) {
  const refusal = useAtomValue(creditAllowanceRefusalAtom, {
    store: getDefaultStore(),
  });
  return (
    <CreditAllowanceBannerNudge nudgeNonce={refusal?.nudgeNonce ?? 0}>
      <AgentCreditAllowanceBanner
        className={
          placement === 'composer' ? AGENT_CREDIT_ALLOWANCE_IN_CHAT_GAP_CLASS : undefined
        }
      />
    </CreditAllowanceBannerNudge>
  );
}
